import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { useProject } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { RoleBadge, StatusBadge, fmtDate, fmtDateTime, fmtMoney } from "@/components/badges";
import { AlertTriangle, ArrowRight, Banknote, ClipboardList, FileDown, FileSpreadsheet, FileWarning, Gauge, Layers, MessageSquare, MessagesSquare, TrendingUp } from "lucide-react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChatBox } from "@/components/ChatBox";
import { useTranslation } from "react-i18next";

const HERO =
  "https://images.unsplash.com/photo-1711962135760-ec08ef3ddbef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxzdXNwZW5kZWQlMjBzd2ltbWluZyUyMHBvb2wlMjBjb25zdHJ1Y3Rpb24lMjBhcmNoaXRlY3R1cmUlMjBsdXh1cnl8ZW58MHx8fHwxNzg4NDMyMDIwfDA&ixlib=rb-4.1.0&q=85";

const SCURVE_TICK_MONO = { fontSize: 10, fontFamily: "JetBrains Mono" };
const SCURVE_TICK = { fontSize: 10 };

export default function Dashboard() {
  const { selectedId, projects, setSelectedId } = useProject();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/dashboard`)).data,
    enabled: !!selectedId,
  });
  const { data: scurve } = useQuery({
    queryKey: ["s-curve", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/s-curve`)).data,
    enabled: !!selectedId,
  });
  const { data: allProjects = [] } = useQuery({
    queryKey: ["admin-projects-overview"],
    queryFn: async () => (await api.get("/admin/projects")).data,
    enabled: !!(user?.is_super_admin || user?.is_admin),
  });
  const { data: myPlans } = useQuery({
    queryKey: ["activity-plans", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/activity-plans`)).data,
    enabled: !!selectedId && !!user && !user.is_super_admin && !user.is_admin,
    retry: false,
  });
  const needsPlan =
    !!myPlans &&
    (myPlans.my_party_ids || []).length > 0 &&
    !(myPlans.items || []).some((i) => (myPlans.my_party_ids || []).includes(i.party_id) && i.status !== "draft" && i.status !== "superseded");

  const exportReport = async (fmt) => {
    try {
      const res = await api.get(`/projects/${selectedId}/export`, { params: { format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `status-report.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${t("dashboard.reportExported")} (${fmt.toUpperCase()})`);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!selectedId && projects.length === 0) {
    return (
      <Card data-testid="dashboard-empty">
        <CardContent className="py-12 text-center">
          <p className="text-slate-600">{t("dashboard.noProjects")}</p>
          {(user?.is_super_admin || user?.is_admin) && (
            <Link to="/admin">
              <Button className="mt-4" data-testid="dashboard-goto-admin">{t("dashboard.gotoAdmin")}</Button>
            </Link>
          )}
        </CardContent>
      </Card>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4" data-testid="dashboard-loading">
        <Skeleton className="h-40 w-full" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" />
        </div>
      </div>
    );
  }

  const overdueItems = [
    ...data.overdue_requests.map((r) => ({ ...r, label: r.title, sub: `${t("dashboard.fileRequest")} · ${r.category} · ${t("dashboard.from")} ${r.target?.company_name}` })),
    ...data.overdue_rfis.map((r) => ({ ...r, label: `${r.rfi_no}: ${r.subject}`, sub: `${t("dashboard.rfi")} · ${r.priority} ${t("dashboard.priority")} · ${t("dashboard.to")} ${r.to_party?.company_name}` })),
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="relative overflow-hidden rounded-xl border bg-slate-900 text-white shadow-sm">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative z-10 p-6 lg:p-8">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="dashboard-project-name">{data.project.name}</h1>
            <StatusBadge status={data.project.status} />
          </div>
          <p className="mt-1 max-w-xl text-sm text-slate-300">{data.project.description}</p>
          <div className="mt-5 max-w-lg">
            <div className="flex items-baseline justify-between">
              <span className="font-mono text-xs uppercase tracking-wider text-slate-400">{t("dashboard.overallProgress")}</span>
              <span className="font-mono text-2xl font-bold text-amber-400" data-testid="dashboard-overall-progress">{data.overall_progress}%</span>
            </div>
            <ProgressBar value={data.overall_progress} className="mt-2 h-2.5 bg-slate-700" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {data.my_parties.map((p) => (
              <RoleBadge key={p.id} role={`${p.company_name} · ${p.role}`} className="border-slate-600" />
            ))}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="border-slate-500 bg-transparent text-white hover:bg-slate-700 hover:text-white" onClick={() => exportReport("pdf")} data-testid="export-report-pdf">
              <FileDown className="mr-1.5 h-4 w-4" /> {t("dashboard.exportPdf")}
            </Button>
            <Button size="sm" variant="outline" className="border-slate-500 bg-transparent text-white hover:bg-slate-700 hover:text-white" onClick={() => exportReport("xlsx")} data-testid="export-report-xlsx">
              <FileSpreadsheet className="mr-1.5 h-4 w-4" /> {t("dashboard.exportExcel")}
            </Button>
          </div>
        </div>
      </div>

      {needsPlan && (
        <Card className="border-amber-300 bg-amber-50" data-testid="plan-onboarding-banner">
          <CardContent className="flex flex-wrap items-center gap-3 p-4">
            <ClipboardList className="h-6 w-6 shrink-0 text-amber-600" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-amber-900">{t("dashboard.planBannerTitle")}</div>
              <div className="mt-0.5 text-xs text-amber-800">
                {t("dashboard.planBannerBody")}
              </div>
            </div>
            <Link to="/progress">
              <Button size="sm" className="bg-amber-600 text-white hover:bg-amber-700" data-testid="plan-onboarding-goto">{t("dashboard.createActivityPlan")}</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {(user?.is_super_admin || user?.is_admin) && allProjects.length > 0 && (
        <Card data-testid="all-projects-overview">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Layers className="h-4 w-4 text-sky-600" /> {t("dashboard.allProjects")}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("dashboard.project")}</TableHead>
                  <TableHead>{t("dashboard.status")}</TableHead>
                  <TableHead className="w-56">{t("dashboard.progressWeighted")}</TableHead>
                  <TableHead>{t("dashboard.parties")}</TableHead>
                  <TableHead>{t("dashboard.overdue")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allProjects.map((p) => (
                  <TableRow key={p.id} className={p.id === selectedId ? "bg-sky-50/60" : "cursor-pointer"} onClick={() => setSelectedId(p.id)} data-testid={`overview-project-${p.id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell><StatusBadge status={p.status} /></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <ProgressBar value={p.overall_progress} className="h-1.5 flex-1" />
                        <span className="font-mono text-xs font-semibold" data-testid={`overview-progress-${p.id}`}>{p.overall_progress}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{p.party_count}</TableCell>
                    <TableCell>
                      {p.overdue_requests + p.overdue_rfis > 0 ? (
                        <span className="font-mono text-xs font-semibold text-rose-700" data-testid={`overview-overdue-${p.id}`}>
                          {p.overdue_requests} {t("dashboard.reqShort")} / {p.overdue_rfis} {t("dashboard.rfi")}
                        </span>
                      ) : (
                        <span className="font-mono text-xs text-emerald-700">{t("dashboard.none")}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card data-testid="metric-open-rfis">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500"><MessageSquare className="h-4 w-4" /><span className="font-mono text-xs uppercase tracking-wider">{t("dashboard.openRfis")}</span></div>
            <div className="mt-2 font-mono text-2xl font-bold text-slate-900">{data.counts.open_rfis}</div>
          </CardContent>
        </Card>
        <Card data-testid="metric-pending-requests">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-slate-500"><FileWarning className="h-4 w-4" /><span className="font-mono text-xs uppercase tracking-wider">{t("dashboard.pendingRequests")}</span></div>
            <div className="mt-2 font-mono text-2xl font-bold text-slate-900">{data.counts.pending_requests}</div>
          </CardContent>
        </Card>
        <Card className="border-rose-200" data-testid="metric-overdue-requests">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-rose-600"><AlertTriangle className="h-4 w-4" /><span className="font-mono text-xs uppercase tracking-wider">{t("dashboard.overdueRequests")}</span></div>
            <div className="mt-2 font-mono text-2xl font-bold text-rose-600">{data.counts.overdue_requests}</div>
          </CardContent>
        </Card>
        <Card className="border-rose-200" data-testid="metric-overdue-rfis">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-rose-600"><AlertTriangle className="h-4 w-4" /><span className="font-mono text-xs uppercase tracking-wider">{t("dashboard.overdueRfis")}</span></div>
            <div className="mt-2 font-mono text-2xl font-bold text-rose-600">{data.counts.overdue_rfis}</div>
          </CardContent>
        </Card>
      </div>

      {scurve && scurve.points.length > 0 && (
        <Card data-testid="scurve-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="h-4 w-4 text-amber-600" /> {t("dashboard.scurveTitle")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 w-full" data-testid="scurve-chart">
              <ResponsiveContainer>
                <LineChart data={scurve.points}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={SCURVE_TICK_MONO} />
                  <YAxis domain={[0, 100]} tick={SCURVE_TICK} unit="%" />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Legend />
                  <Line type="monotone" dataKey="planned" stroke="#64748B" strokeDasharray="6 4" dot={false} name={t("dashboard.planned")} />
                  <Line type="monotone" dataKey="actual" stroke="#D97706" strokeWidth={2.5} dot={false} name={t("dashboard.actual")} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex gap-6 font-mono text-xs text-slate-600" data-testid="scurve-today">
              <span>{t("dashboard.todayPlanned")} <b>{scurve.planned_today}%</b></span>
              <span>{t("dashboard.todayActual")} <b className="text-amber-700">{scurve.actual_today}%</b></span>
            </div>
          </CardContent>
        </Card>
      )}

      {data.payments_summary && (
        <Card data-testid="dashboard-payments-summary">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Banknote className="h-4 w-4 text-sky-600" /> {t("dashboard.paymentsSummary")}</CardTitle>
            <Link to="/payments">
              <Button variant="ghost" size="sm" data-testid="dashboard-goto-payments">{t("dashboard.details")} <ArrowRight className="ml-1 h-4 w-4" /></Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <div><div className="font-mono text-xs uppercase tracking-wider text-slate-500">{t("dashboard.ipcClaimed")}</div><div className="mt-1 font-mono text-lg font-bold" data-testid="summary-ipc-total">{fmtMoney(data.payments_summary.total_ipc)}</div></div>
              <div><div className="font-mono text-xs uppercase tracking-wider text-slate-500">{t("dashboard.paymentsMade")}</div><div className="mt-1 font-mono text-lg font-bold" data-testid="summary-payments-total">{fmtMoney(data.payments_summary.total_payments)}</div></div>
              <div><div className="font-mono text-xs uppercase tracking-wider text-slate-500">{t("dashboard.approved")}</div><div className="mt-1 font-mono text-lg font-bold text-amber-600">{fmtMoney(data.payments_summary.approved)}</div></div>
              <div><div className="font-mono text-xs uppercase tracking-wider text-slate-500">{t("dashboard.paid")}</div><div className="mt-1 font-mono text-lg font-bold text-emerald-600">{fmtMoney(data.payments_summary.paid)}</div></div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card data-testid="dashboard-party-progress">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Gauge className="h-4 w-4 text-sky-600" /> {t("dashboard.progressByParty")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.party_progress.length === 0 && <p className="text-sm text-slate-500">{t("dashboard.noWorkItems")}</p>}
            {data.party_progress.map((p) => (
              <div key={p.party_id} className="space-y-1" data-testid={`party-progress-${p.party_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-medium text-slate-800">{p.company_name}</span>
                    <RoleBadge role={p.role} />
                  </div>
                  <span className="font-mono text-sm font-semibold">{p.progress}%</span>
                </div>
                <ProgressBar value={p.progress} className="h-1.5" />
              </div>
            ))}
            {!data.can_view_all && (
              <p className="pt-1 text-xs text-slate-500">{t("dashboard.ownProgressNote")}</p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="dashboard-overdue-list">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><AlertTriangle className="h-4 w-4 text-rose-600" /> {t("dashboard.overdueItems")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {overdueItems.length === 0 && <p className="text-sm text-slate-500" data-testid="overdue-empty">{t("dashboard.overdueEmpty")}</p>}
            {overdueItems.map((o) => (
              <div key={`${o.kind}-${o.id}`} className="rounded-md border border-rose-200 bg-rose-50/60 px-3 py-2" data-testid={`overdue-${o.kind}-${o.id}`}>
                <div className="text-sm font-medium text-slate-900">{o.label}</div>
                <div className="mt-0.5 text-xs text-slate-600">{o.sub}</div>
                <div className="mt-1 font-mono text-xs font-semibold text-rose-700">{t("dashboard.due")} {fmtDate(o.due_date)}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="dashboard-chat-panel">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><MessagesSquare className="h-4 w-4 text-sky-600" /> {t("dashboard.projectChat")}</CardTitle>
          <Link to="/chat">
            <Button variant="ghost" size="sm" data-testid="dashboard-goto-chat">{t("dashboard.openChat")} <ArrowRight className="ml-1 h-4 w-4" /></Button>
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          <ChatBox projectId={selectedId} compact />
        </CardContent>
      </Card>

      <Card data-testid="dashboard-recent-activity">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t("dashboard.recentActivity")}</CardTitle>
        </CardHeader>
        <CardContent>
          {data.recent_activity.length === 0 && <p className="text-sm text-slate-500">{t("dashboard.noActivity")}</p>}
          <div className="divide-y">
            {data.recent_activity.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5" data-testid={`activity-${a.id}`}>
                <span className="font-mono text-xs text-slate-400">{fmtDateTime(a.created_at)}</span>
                <span className="text-sm text-slate-700">
                  <span className="font-medium">{a.user_email}</span> {t("dashboard.activityUpdated")} <span className="font-medium">"{a.item_title}"</span> ({a.party_company})
                  {a.old_progress != null && (
                    <span className="ml-1 font-mono text-xs text-slate-500">{a.old_progress}% → <span className="font-semibold text-sky-700">{a.new_progress}%</span></span>
                  )}
                  {a.old_progress == null && <span className="ml-1 font-mono text-xs text-slate-500">{t("dashboard.activityCreated")}</span>}
                </span>
                {a.note && <span className="w-full text-xs italic text-slate-500">{a.note}</span>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
